import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Slot } from 'radix-ui'

/*
 * The VentaHub Badge (design board 08): a category or attribute — fulfilment
 * type, country code, "New", "Beta". Height 24 (sm 20), radius 8, label 12/17
 * 600, a 16px icon before the word. No border, no shadow, never a pill.
 *
 * For a lifecycle state use `StatusBadge`, which picks the tone from the one
 * status map; the tone variants here exist for it. `default` is the primary
 * badge — something new or selected, never a status.
 *
 * `chart1`…`chart8` are categorical: a kind of thing (a ticket sub-type), not
 * a state, in the chart series' hues as a tint with readable text.
 */
const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-sm border border-transparent font-semibold whitespace-nowrap [&>svg]:pointer-events-none [&>svg]:size-4',
  {
    variants: {
      variant: {
        default: 'bg-primary-subtle text-primary-text [a&]:hover:bg-primary-subtle-strong',
        secondary: 'bg-surface-muted text-foreground-secondary',
        destructive: 'bg-error-subtle text-error-foreground',
        outline: 'border-border bg-surface text-foreground-secondary',
        ghost: 'text-foreground-secondary [a&]:hover:bg-surface-muted',
        link: 'text-primary-text underline-offset-3 [a&]:hover:underline',
        success: 'bg-success-subtle text-success-foreground',
        warning: 'bg-warning-subtle text-warning-foreground',
        error: 'bg-error-subtle text-error-foreground',
        info: 'bg-info-subtle text-info-foreground',
        neutral: 'bg-neutral-subtle text-neutral-foreground',
        chart1: 'bg-chart-1-subtle text-chart-1-foreground',
        chart2: 'bg-chart-2-subtle text-chart-2-foreground',
        chart3: 'bg-chart-3-subtle text-chart-3-foreground',
        chart4: 'bg-chart-4-subtle text-chart-4-foreground',
        chart5: 'bg-chart-5-subtle text-chart-5-foreground',
        chart6: 'bg-chart-6-subtle text-chart-6-foreground',
        chart7: 'bg-chart-7-subtle text-chart-7-foreground',
        chart8: 'bg-chart-8-subtle text-chart-8-foreground',
      },
      size: {
        default: 'h-6 px-2 text-xs',
        sm: 'h-5 px-1.5 text-2xs',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Badge({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span'

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
